import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { components } from '@worldathletics/api-client';

export type AuthUser = components['schemas']['UserResponseDto'];

// `session` is the opaque Cognito challenge session returned by /auth/login
// when a NEW_PASSWORD_REQUIRED challenge is issued. Source the type from the
// generated BE schema so any BE-side change surfaces here at compile time.
type ChallengeSession =
  components['schemas']['ChallengeResponseDto']['session'];

export type ForceChangeContext = {
  email: string;
  session: ChallengeSession;
};

export type AuthStatus =
  | 'idle'
  | 'loading'
  | 'authenticated'
  | 'force-change-password'
  | 'unauthenticated';

export type AuthState = {
  user: AuthUser | null;
  status: AuthStatus;
  forceChangeContext: ForceChangeContext | null;
};

const initialState: AuthState = {
  user: null,
  status: 'idle',
  forceChangeContext: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.status = 'authenticated';
      state.forceChangeContext = null;
    },
    setForceChangePassword(state, action: PayloadAction<ForceChangeContext>) {
      state.forceChangeContext = action.payload;
      state.status = 'force-change-password';
    },
    setLoading(state) {
      state.status = 'loading';
    },
    setUnauthenticated(state) {
      state.user = null;
      state.status = 'unauthenticated';
      state.forceChangeContext = null;
    },
    logout(state) {
      state.user = null;
      state.status = 'unauthenticated';
      state.forceChangeContext = null;
    },
  },
});

export const {
  setUser,
  setForceChangePassword,
  setLoading,
  setUnauthenticated,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
